/**
 * B-spline / Bezier utilities for blade geometry.
 */

// ── General clamped B-spline (de Boor) ───────────────────────────────────────

function clampedKnots(n, degree) {
  const m = n + degree + 1;
  const k = new Array(m + 1).fill(0);
  const inner = n - degree + 1;
  for (let i = degree + 1; i <= n; i++) k[i] = (i - degree) / inner;
  for (let i = n + 1; i <= m; i++) k[i] = 1.0;
  return k;
}

function deBoor(degree, knots, cps, t) {
  let span = -1;
  for (let i = degree; i < knots.length - degree - 1; i++) {
    if (t >= knots[i] && t < knots[i + 1]) { span = i; break; }
  }
  if (span === -1) span = knots.length - degree - 2;
  const d = cps.slice(span - degree, span + 1).map(p => [p[0], p[1]]);
  for (let r = 1; r <= degree; r++) {
    for (let j = degree; j >= r; j--) {
      const denom = knots[span + 1 + j - r] - knots[span - degree + j];
      const alpha = denom < 1e-14 ? 0 : (t - knots[span - degree + j]) / denom;
      d[j][0] = (1 - alpha) * d[j - 1][0] + alpha * d[j][0];
      d[j][1] = (1 - alpha) * d[j - 1][1] + alpha * d[j][1];
    }
  }
  return d[degree];
}

export function evaluateBSpline(controlPoints, degree = 3, nPts = 60) {
  const n = controlPoints.length - 1;
  if (n < 0) return [];
  const deg = Math.min(degree, n);
  const knots = clampedKnots(n, deg);
  const result = [];
  for (let i = 0; i <= nPts; i++) {
    const t = i === nPts ? 1 - 1e-10 : i / nPts;
    result.push(deBoor(deg, knots, controlPoints, t));
  }
  return result;
}

// ── Engineering MECO parametrisation ─────────────────────────────────────────
//
// Each construction line is defined by:
//   • Inlet straight  : vertical segment at r=le_r from z=inlet_z_start to z=le_z
//   • Cubic B-spline  : degree-3 clamped, 4 CPs — P0=LE, P1, P2, P3=TE
//       P1 = LE + L1 * (sin α_LE, cos α_LE)   — α_LE from axial (z), toward radial (r)
//       P2 = TE - L2 * (cos α_TE, sin α_TE)   — α_TE from radial (r), toward axial (z)
//       Knot vector [0,0,0,0,1,1,1,1] — evaluated via de Boor recursion
//   • Outlet straight : horizontal segment at z=te_z from r=te_r to r=te_r+l_out
//
// params = { le_r, le_z, inlet_z_start,
//            l1, alpha_le,             ← alpha_le default 0 → purely axial tangent at LE
//            l2, alpha_te,             ← alpha_te default 0 → purely radial tangent at TE
//            te_r, te_z, l_out }

export function bezierControlPoints(params) {
  const { le_r, le_z, l1, l2, te_r, te_z } = params;
  const a_le = ((params.alpha_le ?? 0) * Math.PI) / 180;
  const a_te = ((params.alpha_te ?? 0) * Math.PI) / 180;
  return [
    [+le_r,                           +le_z],
    [+le_r + +l1 * Math.sin(a_le),    +le_z + +l1 * Math.cos(a_le)],
    [+te_r - +l2 * Math.cos(a_te),    +te_z - +l2 * Math.sin(a_te)],
    [+te_r,                           +te_z],
  ];
}

const MECO_KNOTS = [0, 0, 0, 0, 1, 1, 1, 1]; // clamped degree-3, 4 CPs

/**
 * Linearly interpolate all engineering MECO params between hub (t=0) and shroud (t=1).
 */
export function interpolateMecoParams(hubParams, shroudParams, t) {
  const lerp = (a, b) =>
    (a != null && b != null && a !== "" && b !== "") ? +a + t * (+b - +a) : null;
  return {
    le_r:          lerp(hubParams.le_r,          shroudParams.le_r),
    le_z:          lerp(hubParams.le_z,          shroudParams.le_z),
    inlet_z_start: lerp(hubParams.inlet_z_start, shroudParams.inlet_z_start),
    l1:            lerp(hubParams.l1,            shroudParams.l1),
    alpha_le:      lerp(hubParams.alpha_le ?? 0, shroudParams.alpha_le ?? 0),
    l2:            lerp(hubParams.l2,            shroudParams.l2),
    alpha_te:      lerp(hubParams.alpha_te ?? 0, shroudParams.alpha_te ?? 0),
    te_r:          lerp(hubParams.te_r,          shroudParams.te_r),
    te_z:          lerp(hubParams.te_z,          shroudParams.te_z),
    l_out:         lerp(hubParams.l_out,         shroudParams.l_out),
  };
}

const valid = v => v != null && v !== "";

/**
 * Compute full (r, z) polyline from engineering MECO spline params.
 * Returns { r: [], z: [] }.
 */
// ── Radial Diffusor Channel parametrisation ──────────────────────────────────
//
// 14 parameters per stream surface (hub / shroud):
//   Inlet:      R3, Z3   — start point (on inlet tangent line)
//               R4, γ4   — arc-start radius + inlet tangent angle from radial
//   Outer apex: R5, Z5, L5 — apex radius, axial centre, straight length
//   Return:     R6, Z6   — return tangent-line anchor
//               R7, γ7   — return straight end + angle from radial
//   Outlet:     R8, Z8, L9 — outlet radius, axial pos., outlet straight length
//
// Tangent lines:
//   Inlet:  z(r) = Z3 + (r − R3)·tan(γ4)   — P3, P4, CP34 lie on this line
//   Return: z(r) = Z6 − (r − R6)·tan(γ7)   — CP56, P6, P7, CP78 lie on this line
//
// Segments (connected, no duplicate vertices):
//   P3→P4 (straight) → P4→P5a (quad B-spline via CP34)
//   → P5a→P5b (outer straight) → P5b→P6 (quad B-spline via CP56)
//   → P6→P7 (straight) → P7→P8 (quad B-spline via CP78) → P8→P9 (outlet straight)

export function polylineFromDiffusorSpline(params, nPts = 40) {
  const { R3, Z3, R4, gamma4, R5, Z5, Z5b, R6, Z6, R7, gamma7, R8, Z8, L9 } = params || {};
  const ok = v => v != null && v !== "" && !isNaN(+v);
  if (![R3, Z3, R4, gamma4, R5, Z5, Z5b, R6, Z6, R7, gamma7, R8, Z8, L9].every(ok))
    return { r: [], z: [] };

  const g4 = (+gamma4 * Math.PI) / 180;
  const g7 = (+gamma7 * Math.PI) / 180;
  const z_in  = r => +Z3 + (+r - +R3) * Math.tan(g4);
  const z_ret = r => +Z6 - (+r - +R6) * Math.tan(g7);

  const P3   = [+R3, +Z3];
  const P4   = [+R4, z_in(R4)];
  const CP34 = [+R5, z_in(R5)];
  const P5a  = [+R5, +Z5];    // upper apex — direct coordinate
  const P5b  = [+R5, +Z5b];   // lower apex — direct coordinate
  const CP56 = [+R5, z_ret(R5)];
  const P6   = [+R6, +Z6];
  const P7   = [+R7, z_ret(R7)];
  const CP78 = [+R8, z_ret(R8)];
  const P8   = [+R8, +Z8];
  const P9   = [+R8, +Z8 + +L9];

  const r = [], z = [];
  const push = ([pr, pz]) => { r.push(pr); z.push(pz); };
  const quadBez = (A, B, C, skip1 = false) => {
    for (let i = skip1 ? 1 : 0; i <= nPts; i++) {
      const t = i / nPts, mt = 1 - t;
      push([mt*mt*A[0] + 2*mt*t*B[0] + t*t*C[0],
            mt*mt*A[1] + 2*mt*t*B[1] + t*t*C[1]]);
    }
  };

  push(P3);
  push(P4);
  quadBez(P4, CP34, P5a, true);
  push(P5b);
  quadBez(P5b, CP56, P6, true);
  push(P7);
  quadBez(P7, CP78, P8, true);
  push(P9);

  return { r, z };
}

export function interpolateDiffusorParams(hubP, shrP, t) {
  const lerp = (a, b) =>
    (a != null && b != null && a !== "" && b !== "") ? +a + t * (+b - +a) : null;
  return {
    R3: lerp(hubP.R3, shrP.R3), Z3: lerp(hubP.Z3, shrP.Z3),
    R4: lerp(hubP.R4, shrP.R4), gamma4: lerp(hubP.gamma4 ?? 0, shrP.gamma4 ?? 0),
    R5: lerp(hubP.R5, shrP.R5), Z5: lerp(hubP.Z5, shrP.Z5), L5: lerp(hubP.L5, shrP.L5),
    R6: lerp(hubP.R6, shrP.R6), Z6: lerp(hubP.Z6, shrP.Z6),
    R7: lerp(hubP.R7, shrP.R7), gamma7: lerp(hubP.gamma7 ?? 0, shrP.gamma7 ?? 0),
    R8: lerp(hubP.R8, shrP.R8), Z8: lerp(hubP.Z8, shrP.Z8), L9: lerp(hubP.L9, shrP.L9),
  };
}

export function polylineFromMecoSpline(params, nCurvePts = 60) {
  const { le_r, le_z, inlet_z_start, l1, l2, te_r, te_z, l_out } = params || {};

  const r = [], z = [];

  // 1. Inlet straight (vertical)
  if (valid(le_r) && valid(inlet_z_start) && valid(le_z)) {
    r.push(+le_r); z.push(+inlet_z_start);
    r.push(+le_r); z.push(+le_z);
  }

  // 2. Cubic B-spline (degree 3, clamped, 4 CPs — de Boor)
  if (valid(le_r) && valid(le_z) && valid(l1) && valid(l2) && valid(te_r) && valid(te_z)) {
    const cps = bezierControlPoints(params);
    for (let i = (r.length > 0 ? 1 : 0); i <= nCurvePts; i++) {
      const t = i === nCurvePts ? 1 - 1e-10 : i / nCurvePts;
      const pt = deBoor(3, MECO_KNOTS, cps, t);
      r.push(pt[0]); z.push(pt[1]);
    }
  }

  // 3. Outlet straight (horizontal)
  if (valid(te_r) && valid(te_z) && valid(l_out)) {
    if (r.length === 0) { r.push(+te_r); z.push(+te_z); }
    r.push(+te_r + +l_out); z.push(+te_z);
  }

  return { r, z };
}
